import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Upload, Loader2, X, FileText, Trash2, Plus } from 'lucide-react';
import { adminListJobs, adminUploadResumesBatch } from '../../api';
import { useToast } from '../../hooks/use-toast';

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_EXT = ['pdf', 'doc', 'docx'];

const stripExt = (n) => n.replace(/\.[^.]+$/, '');

const UploadResumeModal = ({ open, onOpenChange, onUploaded }) => {
  const { toast } = useToast();
  const [jobs, setJobs] = useState([]);
  const [targetJobId, setTargetJobId] = useState('none');
  // each row: { id, file, name, email, error? }
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const data = await adminListJobs();
        setJobs(data.filter(j => j.id !== 'general-application-default'));
      } catch { /* ignore */ }
    })();
  }, [open]);

  const reset = () => { setTargetJobId('none'); setRows([]); };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    const next = [];
    for (const f of incoming) {
      const ext = f.name.toLowerCase().split('.').pop();
      if (!ALLOWED_EXT.includes(ext)) {
        toast({ title: 'Skipped (unsupported)', description: `${f.name} — use PDF/DOC/DOCX.`, variant: 'destructive' });
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast({ title: 'Skipped (too large)', description: `${f.name} exceeds 5MB.`, variant: 'destructive' });
        continue;
      }
      next.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        name: stripExt(f.name),
        email: '',
      });
    }
    setRows(prev => [...prev, ...next]);
  };

  const onFileChange = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));
  const updateRow = (id, patch) => setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));

  const submit = async (e) => {
    e.preventDefault();
    if (rows.length === 0) { toast({ title: 'Add at least one resume', variant: 'destructive' }); return; }
    // Validate names
    const blank = rows.find(r => !(r.name || '').trim());
    if (blank) { toast({ title: 'Candidate name required', description: `Provide a name for ${blank.file.name}`, variant: 'destructive' }); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      if (targetJobId !== 'none') fd.append('target_job_id', targetJobId);
      // ORDER must align with files
      rows.forEach(r => {
        fd.append('candidate_names', r.name.trim());
        fd.append('candidate_emails', (r.email || '').trim());
        fd.append('files', r.file, r.file.name);
      });
      const res = await adminUploadResumesBatch(fd);
      const okCount = res.uploaded?.length || 0;
      const failCount = res.failed?.length || 0;
      if (okCount > 0) {
        toast({
          title: failCount === 0 ? `Uploaded ${okCount} resume${okCount > 1 ? 's' : ''}` : `Uploaded ${okCount}, ${failCount} failed`,
          description: failCount > 0 ? res.failed.map(f => `${f.filename}: ${f.error}`).join(' • ') : undefined,
          variant: failCount > 0 ? 'destructive' : 'default',
        });
        onUploaded && onUploaded(res.uploaded);
        reset();
        onOpenChange(false);
      } else {
        toast({
          title: 'No resumes uploaded',
          description: (res.failed || []).map(f => `${f.filename}: ${f.error}`).join(' • ') || 'All items failed.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-[#0d1424] border border-cyan-400/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="upload-resume-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white inline-flex items-center gap-2">
            <Upload className="w-5 h-5 text-cyan-400" /> Upload Resume{rows.length > 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Drop one or many PDF/DOC/DOCX files. They're added to the resume repository and become searchable. Linking a job is optional and lets you run AI analysis against it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-slate-300 text-xs mb-1.5 block">Job (optional)</Label>
            <Select value={targetJobId} onValueChange={setTargetJobId}>
              <SelectTrigger className="bg-[#080d18] border-slate-700 text-white" data-testid="target-job-select">
                <SelectValue placeholder="Select a position to compare against..." />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1424] border-slate-700 text-white max-h-72">
                <SelectItem value="none" data-testid="target-job-none">No specific job (keep on file)</SelectItem>
                {jobs.map(j => (
                  <SelectItem key={j.id} value={j.id}>
                    <span className="font-mono text-cyan-300 text-xs mr-2">{j.job_number}</span>
                    {j.title}
                    {j.location && <span className="text-slate-500 text-xs ml-2">— {j.location}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-slate-300 text-xs mb-1.5 block">
              Resume Files <span className="text-slate-500">(PDF, DOC, DOCX · max 5MB each · multi-select supported)</span>
            </Label>
            <label className="flex items-center gap-3 cursor-pointer rounded-md border border-dashed border-slate-700 hover:border-cyan-400 bg-[#080d18] px-4 py-3 transition">
              <Plus className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-slate-300 flex-1">
                {rows.length === 0 ? 'Click to select resumes (hold Ctrl/Cmd to pick multiple)' : `Add more resumes (${rows.length} selected)`}
              </span>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx"
                onChange={onFileChange}
                className="hidden"
                data-testid="batch-files-input"
              />
            </label>
          </div>

          {rows.length > 0 && (
            <div className="rounded-md border border-cyan-400/10 bg-slate-900/30 overflow-hidden">
              <div className="px-3 py-2 border-b border-cyan-400/10 text-xs uppercase tracking-wider text-slate-400 flex items-center justify-between">
                <span>{rows.length} candidate{rows.length > 1 ? 's' : ''} ready to upload</span>
                <button type="button" onClick={() => setRows([])} className="text-slate-500 hover:text-red-300 text-xs underline">Clear all</button>
              </div>
              <div className="divide-y divide-cyan-400/5 max-h-72 overflow-y-auto">
                {rows.map((r, idx) => (
                  <div key={r.id} className="p-3 grid grid-cols-12 gap-2 items-start" data-testid={`batch-row-${idx}`}>
                    <div className="col-span-12 flex items-center gap-2 text-xs text-slate-400">
                      <FileText className="w-3.5 h-3.5 text-cyan-400" />
                      <span className="truncate flex-1">{r.file.name}</span>
                      <span className="text-slate-600">{(r.file.size / 1024).toFixed(0)} KB</span>
                      <button type="button" onClick={() => removeRow(r.id)} className="text-slate-500 hover:text-red-300" title="Remove">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="col-span-12 sm:col-span-6">
                      <Input
                        required
                        placeholder="Candidate name"
                        value={r.name}
                        onChange={e => updateRow(r.id, { name: e.target.value })}
                        className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white h-8 text-sm"
                        data-testid={`batch-row-name-${idx}`}
                      />
                    </div>
                    <div className="col-span-12 sm:col-span-6">
                      <Input
                        type="email"
                        placeholder="Email (optional)"
                        value={r.email}
                        onChange={e => updateRow(r.id, { email: e.target.value })}
                        className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white h-8 text-sm"
                        data-testid={`batch-row-email-${idx}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" onClick={() => { reset(); onOpenChange(false); }} variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200" data-testid="upload-cancel-btn">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button type="submit" disabled={submitting || rows.length === 0} className="bg-cyan-400 hover:bg-cyan-300 disabled:opacity-70 text-slate-900 font-semibold" data-testid="upload-submit-btn">
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading {rows.length}...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Upload {rows.length || ''} Resume{rows.length === 1 ? '' : 's'}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadResumeModal;
